import React, { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Menu,
  Typography,
  Divider,
  CircularProgress,
  TextField,
  InputAdornment,
} from "@mui/material";
import { 
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { observer } from "mobx-react-lite";
import { store } from "../../../app/stores/store";
import { Category } from "../../../app/stores/productStore";

interface CategoryFilterProps {
  selectedCategories: number[];
  onCategoryChange: (categoryIds: number[]) => void;
  onApplyFilter: () => void;
  onClearFilters?: (clearType: 'all' | 'subcategories') => void;
}

const CategoryFilter: React.FC<CategoryFilterProps> = observer(({
  selectedCategories,
  onCategoryChange,
  onApplyFilter,
  onClearFilters,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const open = Boolean(anchorEl);

  const { categories, loadingCategories } = store.productStore;

  // Filter categories based on search query
  const filteredCategories = categories.filter((category) =>
    category.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchQuery("");
  };

  const handleCategoryToggle = (categoryId: number) => {
    if (selectedCategories.includes(categoryId)) {
      onCategoryChange(selectedCategories.filter((id) => id !== categoryId));
    } else {
      onCategoryChange([...selectedCategories, categoryId]);
    }
  };

  const clearAll = () => {
    if (onClearFilters) {
      onClearFilters('all');
    } else {
      onCategoryChange([]);
    }
  };

  const applyFilter = () => {
    onApplyFilter();
    handleClose();
  };

  return (
    <Box sx={{ mb: { xs: 1, md: 0 } }}>
      <Button
        variant="outlined"
        onClick={handleClick}
        endIcon={<KeyboardArrowDownIcon />}
        sx={{
          textTransform: "none",
          color: "#000",
          borderColor: "#e0e0e0",
          backgroundColor: "#fff",
          borderRadius: 1,
          px: 2,
          py: 1,
          "&:hover": {
            backgroundColor: "#f5f5f5",
            borderColor: "#e0e0e0",
          },
        }}
      >
        Categories {selectedCategories.length > 0 && `(${selectedCategories.length})`}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            minWidth: 280,
            mt: 1,
            borderRadius: 2,
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
          },
        }}
        MenuListProps={{
          sx: {
            padding: 0,
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 2,
            py: 1,
          }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            Categories
          </Typography>
          {selectedCategories.length > 0 && (
            <Button
              size="small"
              onClick={clearAll}
              sx={{
                textTransform: "none",
                color: "#1976d2",
                fontWeight: 500,
                minWidth: "auto",
                p: 0.5,
                "&:hover": {
                  backgroundColor: "transparent",
                  textDecoration: "underline",
                },
              }}
            >
              Clear All
            </Button>
          )}
        </Box>

        <Divider />

        {/* Search Input */}
        <Box sx={{ px: 2, py: 1.5 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search categories..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 1,
              },
            }}
          />
        </Box>

        <Divider />

        {/* Category List */}
        <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
          {loadingCategories ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 4,
              }}
            >
              <CircularProgress size={32} />
            </Box>
          ) : filteredCategories.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ px: 2, py: 2, textAlign: "center" }}
            >
              {searchQuery ? "No categories found" : "No categories available"}
            </Typography>
          ) : (
            filteredCategories.map((category: Category) => (
              <Box
                key={category.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 2,
                  py: 1,
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                  },
                }}
                onClick={() => handleCategoryToggle(category.id)}
              >
                <Typography variant="body2">{category.name}</Typography>
                <Checkbox
                  size="small"
                  checked={selectedCategories.includes(category.id)}
                  onChange={() => handleCategoryToggle(category.id)}
                  onClick={(e) => e.stopPropagation()}
                />
              </Box>
            ))
          )}
        </Box>

        <Divider />

        {/* Footer with Apply Filter */}
        <Box sx={{ p: 1.5 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={applyFilter}
            disabled={selectedCategories.length === 0}
            sx={{
              textTransform: "none",
              backgroundColor: "#1976d2",
              "&:hover": {
                backgroundColor: "#1565c0",
              },
            }}
          >
            Apply Filter
          </Button>
        </Box>
      </Menu>
    </Box>
  );
});

export default CategoryFilter;
